import { MutationOptions, WithRequired } from '@tanstack/react-query';

import {
  createSPOptimizerStoreSamples,
  deleteSPOptimizerStoreExcludePhrase,
  deleteSPOptimizerStoreIncludePhrase,
  getSignedEmailScreenshotUploadUrl,
  getSignedLogoUploadUrlForUser,
  getSignedMediaUploadUrlForUseCase,
  patchFlowMessageSetupSkippedReason,
  patchPretesterInboxName,
  patchSPOptimizerFlowMessageExperiment,
  patchSPOptimizerStore,
  patchStoreInfo,
  patchUsecaseOnboardingState,
  postAddPretesterInbox,
  postChangeUiEnabledForSPOptimizerStore,
  postConvertSPOptimizerFlowMessageExperimentToMab,
  postCreateSPOptimizerFlowMessageExperimentFromFlowMessage,
  postCreateSPOptimizerFlowMessageExperimentPhaseFromFlowMessage,
  postCreateSPOptimizerFlowMessageExperimentVariant,
  postCreateSPOptimizerStoreFromFlowMessage,
  postCreateSPOptimizerStoreFromStore,
  postCreateUsecasesFromOnboarding,
  postDeactivateSPOptimizerFlowMessageExperimentVariant,
  postDidUploadEmailScreenshot,
  postDidUploadLogoForUser,
  postDidUploadUsecaseMedia,
  postDisableSPOptimizerFlowMessageExperiment,
  postEnableSPOptimizerFlowMessageExperiment,
  postFeedback,
  postGenerateCampaignMessageSubjectPreviews,
  postSPOptimizerFlowMessageExperimentPhaseSetWinnerShard,
  postSPOptimizerFlowMessageExperimentSetWinnerShard,
  postSPOptimizerStoreBulkCreateNewFlowPhase,
  postSPOptimizerStoreExcludePhrase,
  postSPOptimizerStoreIncludePhrase,
  postSaveSelectedProduct,
  postSaveSpOptimizerLiteSetCustomerEmailAndMessageType,
  postSaveVideoScripts,
  postStoreInfo,
  postTriggerRefreshInvalidSpForExperiment,
  postTriggerVariantGeneration,
  regenerateDescriptionFromAboutPageUrl,
} from './methods';

const makeMutation = <F extends (...a: [...Parameters<F>]) => Promise<Awaited<ReturnType<F>>>>(
  apiMethod: F,
  otherOptions?: Omit<MutationOptions<Awaited<ReturnType<F>>, unknown, Parameters<F>>, 'mutationFn'>,
): WithRequired<MutationOptions<Awaited<ReturnType<F>>, unknown, Parameters<F>>, 'mutationFn'> => {
  return {
    mutationFn: (args) => apiMethod(...args),
    ...(otherOptions ?? {}),
  };
};

export const mutations = {
  signedLogoUploadUrlForUser: makeMutation(getSignedLogoUploadUrlForUser),
  signedEmailScreenshotUploadUrl: makeMutation(getSignedEmailScreenshotUploadUrl),
  didUploadEmailScreenshot: makeMutation(postDidUploadEmailScreenshot),
  triggerVariantGeneration: makeMutation(postTriggerVariantGeneration),
  signedMediaUploadUrlForUseCase: makeMutation(getSignedMediaUploadUrlForUseCase),
  didUploadUseCaseMedia: makeMutation(postDidUploadUsecaseMedia),
  didUploadLogoForUser: makeMutation(postDidUploadLogoForUser),
  usecaseOnboardingState: makeMutation(patchUsecaseOnboardingState),
  saveVideoScript: makeMutation(postSaveVideoScripts),
  saveSpOptimizerLiteSetCustomerEmailAndMessageType: makeMutation(
    postSaveSpOptimizerLiteSetCustomerEmailAndMessageType,
  ),
  createSPOptimizerStoreFromFlowMessage: makeMutation(postCreateSPOptimizerStoreFromFlowMessage),
  createSPOptimizerStoreFromStore: makeMutation(postCreateSPOptimizerStoreFromStore),
  updateSPOptimizerStoreAboutPageUrl: makeMutation(regenerateDescriptionFromAboutPageUrl),
  createSPOptimizerStoreBulkCreateNewFlowPhase: makeMutation(postSPOptimizerStoreBulkCreateNewFlowPhase),
  createSPOptimizerFlowMessageExperimentFromFlowMessage: makeMutation(
    postCreateSPOptimizerFlowMessageExperimentFromFlowMessage,
  ),
  convertSPOptimizerFlowMessageExperimentToMab: makeMutation(postConvertSPOptimizerFlowMessageExperimentToMab),
  enableSPOptimizerFlowMessageExperiment: makeMutation(postEnableSPOptimizerFlowMessageExperiment),
  disableSPOptimizerFlowMessageExperiment: makeMutation(postDisableSPOptimizerFlowMessageExperiment),
  createSPOptimizerFlowMessageExperimentPhaseFromFlowMessage: makeMutation(
    postCreateSPOptimizerFlowMessageExperimentPhaseFromFlowMessage,
  ),
  updateSPOptimizerFlowMessageExperimentSetWinnerShard: makeMutation(
    postSPOptimizerFlowMessageExperimentSetWinnerShard,
  ),
  updateSPOptimizerFlowMessageExperimentPhaseSetWinnerShard: makeMutation(
    postSPOptimizerFlowMessageExperimentPhaseSetWinnerShard,
  ),
  updateSPOptimizerStoreUiEnabled: makeMutation(postChangeUiEnabledForSPOptimizerStore),
  createStoreInfo: makeMutation(postStoreInfo),
  createSPOptimizerStoreExcludePhrase: makeMutation(postSPOptimizerStoreExcludePhrase),
  createSPOptimizerFlowMessageExperimentVariant: makeMutation(postCreateSPOptimizerFlowMessageExperimentVariant),
  deactivateSPOptimizerFlowMessageExperimentVariant: makeMutation(
    postDeactivateSPOptimizerFlowMessageExperimentVariant,
  ),
  triggerRefreshInvalidSpForExperiment: makeMutation(postTriggerRefreshInvalidSpForExperiment),
  createSPOptimizerStoreIncludePhrase: makeMutation(postSPOptimizerStoreIncludePhrase),
  deleteSPOptimizerStoreExcludePhrase: makeMutation(deleteSPOptimizerStoreExcludePhrase),
  deleteSPOptimizerStoreIncludePhrase: makeMutation(deleteSPOptimizerStoreIncludePhrase),
  updateStoreInfo: makeMutation(patchStoreInfo),
  updateSPOptimizerStore: makeMutation(patchSPOptimizerStore),
  updateSPOptimizerFlowMessageExperiment: makeMutation(patchSPOptimizerFlowMessageExperiment),
  createUseCasesFromOnboarding: makeMutation(postCreateUsecasesFromOnboarding),
  createFeedback: makeMutation(postFeedback),
  createSPOptimizerStoreSamples: makeMutation(createSPOptimizerStoreSamples),
  saveSelectedProduct: makeMutation(postSaveSelectedProduct),
  generateCampaignMessageSubjectPreviews: makeMutation(postGenerateCampaignMessageSubjectPreviews),
  updateFlowMessageSetupSkippedReason: makeMutation(patchFlowMessageSetupSkippedReason),
  addPretesterInbox: makeMutation(postAddPretesterInbox),
  updatePretesterInboxName: makeMutation(patchPretesterInboxName),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} satisfies Record<string, WithRequired<MutationOptions<any, unknown, any>, 'mutationFn'>>;
